import { emptySplitApi } from "./emptyApi"

/**
 * Meta supplemental-feed helpers. The feed itself is a public tokened CSV the
 * backend serves to Meta; this endpoint returns (minting on first call) the
 * company's feed URL so the UI can hand it to Commerce Manager.
 */

/** One creative in the supplemental feed's slot layout. `tag` is the Meta
 * media tag: the asset id for AdForge customs, a taxonomy word
 * (lifestyle/detail/model_studio/static) for snapshot image sets. */
export interface FeedCreative {
  tag: string
  url: string
}

export interface FeedCustomCreative extends FeedCreative {
  assetId: string
  /** True while the asset's file is still downloading into the media bucket
   * (a just-saved AdForge creative) — selected, but not in the CSV yet. */
  pending: boolean
}

/** Snapshot image; `assetId` is set when the URL resolves to a library asset
 * (then it can be selected into the feed with a click). */
export interface FeedSnapshotCreative extends FeedCreative {
  assetId?: string
}

export interface ProductFeedMedia {
  /** Shopify product id — or a color ref `<productId>_<color>` for one
   * colorway's selection (every size of that color shares it). */
  productRefId: string
  /** False when the item isn't in the company's feed snapshot —
   * Meta could not match a row for it. */
  inSnapshot: boolean
  /** Set when `productRefId` resolved to a colorway / variant rather than a
   * product. `size` is empty for a color ref. */
  variant?: { itemGroupId: string; title: string; color: string; size: string }
  heroImageUrl: string
  /** True once the user picked creatives: the feed serves ONLY the selection
   * instead of auto-padding from the snapshot sets. */
  selectionActive: boolean
  customImages: FeedCustomCreative[]
  customVideos: FeedCustomCreative[]
  snapshotImages: FeedSnapshotCreative[]
  imageSlots: number
  videoSlots: number
}

export const supplementalFeedApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    /** Mutation (not query): the first call mints and stores the token. */
    getSupplementalFeedUrl: builder.mutation<{ url: string }, void>({
      query: () => ({ url: "supplemental-feed/url", method: "POST" }),
    }),
    /** Slot composition the supplemental CSV serves for one product — or one
     * COLORWAY when `productRefId` is a color ref `<productId>_<color>` (the
     * drawer's per-color picker). Tagged with AssetProductTags so
     * save-to-feed / untag mutations refresh it. */
    getProductFeedMedia: builder.query<
      ProductFeedMedia,
      { productRefId: string }
    >({
      query: ({ productRefId }) => ({
        url: `supplemental-feed/products/${encodeURIComponent(productRefId)}/media`,
        method: "GET",
      }),
      providesTags: ["AssetProductTags"],
    }),
    /** Slot order of one ref's selection (images and videos together). */
    reorderProductFeedMedia: builder.mutation<
      { ordered: number },
      { productRefId: string; assetIds: string[] }
    >({
      query: ({ productRefId, assetIds }) => ({
        url: `supplemental-feed/products/${encodeURIComponent(productRefId)}/media/order`,
        method: "PUT",
        body: { assetIds },
      }),
      invalidatesTags: ["AssetProductTags"],
    }),
  }),
})

export const {
  useGetSupplementalFeedUrlMutation,
  useGetProductFeedMediaQuery,
  useReorderProductFeedMediaMutation,
} = supplementalFeedApi
